import { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { playSound, playSoundLoop, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { formatTime } from '~/shared';
import { AppConnectWiresGameField } from '../AppConnectWiresGameField';
import { ResultOverlay } from '../ResultOverlay';
import {
	StyledContainer,
	StyledTimer,
	StyledWrapper,
} from './AppConnectWiresGame.styled';

const AppConnectWiresGame = () => {
	const gameTime = useObservable(rage.jobElectrician.getGameTimeWires());
	const [timeLeft, setTimeLeft] = useState(gameTime);
	const [gameResultReported, setGameResultReported] = useState(false);
	const [gameResult, setGameResult] = useState<'win' | 'lose'>('lose');
	const [gameEnded, setGameEnded] = useState(false);
	const startTimeRef = useRef(Date.now());
	const gameResultRef = useRef<'win' | 'lose' | null>(null);
	const stopSound = useMemo(() => playSoundLoop(Sound.ElectricMusic), []);

	const circuitNumber = useMemo(
		() => Math.floor(Math.random() * 900) + 100,
		[],
	);

	const timeString = useMemo(
		() => formatTime(Math.max(timeLeft, 0), '{mm}:{ss}'),
		[timeLeft],
	);

	const handleGameSolved = () => {
		if (gameResultRef.current) return;
		gameResultRef.current = 'win';
		setGameResult('win');
		setGameEnded(true);
		stopSound();
		playSound(Sound.ElectricFinish);
		setTimeout(() => {
			rage.jobElectrician
				.reportWin()
				.catch((err) => rage.notifications.showException(err));
			setGameResultReported(true);
		}, 1500);
	};

	useEffect(() => {
		if (timeLeft <= 0) {
			if (!gameResultReported && !gameEnded && !gameResultRef.current) {
				gameResultRef.current = 'lose';
				rage.notifications.showError('Вы не успели починить проводку!');
				setGameResult('lose');
				setGameEnded(true);
				stopSound();
				playSound(Sound.ElectricLoss);
				setTimeout(() => {
					rage.jobElectrician
						.reportLoss()
						.catch((err) => rage.notifications.showException(err));
					setGameResultReported(true);
				}, 1500);
			}
			return;
		}

		const updateTime = () => {
			const elapsed = Date.now() - startTimeRef.current;
			const remaining = Math.max(gameTime - elapsed, 0);
			setTimeLeft(remaining);

			if (remaining > 0) {
				setTimeout(updateTime, 1000);
			}
		};

		const timeout = setTimeout(updateTime, 1000);

		return () => clearTimeout(timeout);
	}, [gameTime, gameResultReported, gameEnded, timeLeft]);

	return (
		<StyledWrapper>
			<StyledContainer>
				<Text
					style={{
						fontSize: 20,
						fontWeight: 500,
						color: '#ffffff',
						marginTop: 35,
					}}
				>
					Разрыв проводки №{circuitNumber}
				</Text>
				<StyledTimer>
					<Text>{timeString}</Text>
				</StyledTimer>
				<AppConnectWiresGameField handleGameSolved={handleGameSolved} />
				<Text
					style={{
						fontSize: 13,
						color: '#ffffff',
						opacity: 0.5,
						marginTop: 484,
					}}
				>
					Соедините провода одинакового цвета до истечения времени
				</Text>
			</StyledContainer>
			<CSSTransition
				in={gameEnded}
				timeout={300}
				classNames="overlay"
				unmountOnExit
			>
				<ResultOverlay type={gameResult} />
			</CSSTransition>
		</StyledWrapper>
	);
};

export { AppConnectWiresGame };
